import { addDoc, collection, serverTimestamp } from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantData } from '@/utils/tenantQuery'
import type {
  ActivityLog,
  ActivityLogEntityType,
  ActivityLogAction,
  ActivityLogChange,
} from '@/types'

export function useActivityLog() {
  const authStore = useAuthStore()

  const logActivity = async (
    entityType: ActivityLogEntityType,
    entityId: string,
    action: ActivityLogAction,
    description: string,
    changes?: ActivityLogChange[]
  ): Promise<void> => {
    try {
      const userId = authStore.userId || 'system'

      await addDoc(collection(db, 'activityLogs'), {
        ...getTenantData(),
        entityType,
        entityId,
        action,
        description,
        changes: changes || null,
        createdAt: serverTimestamp(),
        createdBy: userId,
      })
    } catch (error) {
      console.error('Error logging activity:', error)
      // Don't throw - activity logging should not break the main flow
    }
  }

  const logCreate = async (
    entityType: ActivityLogEntityType,
    entityId: string,
    entityName?: string
  ): Promise<void> => {
    const description = entityName
      ? `Se creo ${getEntityLabel(entityType)} "${entityName}"`
      : `Se creo ${getEntityLabel(entityType)}`
    await logActivity(entityType, entityId, 'created', description)
  }

  const logUpdate = async (
    entityType: ActivityLogEntityType,
    entityId: string,
    changes: ActivityLogChange[],
    entityName?: string
  ): Promise<void> => {
    const description = entityName
      ? `Se actualizo ${getEntityLabel(entityType)} "${entityName}"`
      : `Se actualizo ${getEntityLabel(entityType)}`
    await logActivity(entityType, entityId, 'updated', description, changes)
  }

  const logDelete = async (
    entityType: ActivityLogEntityType,
    entityId: string,
    entityName?: string
  ): Promise<void> => {
    const description = entityName
      ? `Se elimino ${getEntityLabel(entityType)} "${entityName}"`
      : `Se elimino ${getEntityLabel(entityType)}`
    await logActivity(entityType, entityId, 'deleted', description)
  }

  const logStatusChange = async (
    entityType: ActivityLogEntityType,
    entityId: string,
    oldStatus: string,
    newStatus: string,
    entityName?: string
  ): Promise<void> => {
    const description = entityName
      ? `Se cambio el estado de ${getEntityLabel(entityType)} "${entityName}" de "${oldStatus}" a "${newStatus}"`
      : `Se cambio el estado de "${oldStatus}" a "${newStatus}"`
    await logActivity(entityType, entityId, 'status_changed', description, [
      { field: 'status', oldValue: oldStatus, newValue: newStatus },
    ])
  }

  const logAssignment = async (
    entityType: ActivityLogEntityType,
    entityId: string,
    assignedToName: string,
    entityName?: string
  ): Promise<void> => {
    const description = entityName
      ? `Se asigno ${getEntityLabel(entityType)} "${entityName}" a ${assignedToName}`
      : `Se asigno a ${assignedToName}`
    await logActivity(entityType, entityId, 'assigned', description)
  }

  const logComment = async (
    entityType: ActivityLogEntityType,
    entityId: string,
    entityName?: string
  ): Promise<void> => {
    const description = entityName
      ? `Se agrego un comentario en ${getEntityLabel(entityType)} "${entityName}"`
      : `Se agrego un comentario`
    await logActivity(entityType, entityId, 'commented', description)
  }

  const getEntityLabel = (entityType: ActivityLogEntityType): string => {
    const labels: Record<ActivityLogEntityType, string> = {
      project: 'el proyecto',
      epic: 'la epica',
      requirement: 'el requerimiento',
      petition: 'la peticion',
      ticket: 'el ticket',
      quotation: 'la cotizacion',
      salesOrder: 'la orden de venta',
      user: 'el usuario',
      organization: 'la organizacion',
      release: 'el release',
      appointment: 'la cita',
      document: 'el documento',
      application: 'la aplicacion',
      branch: 'la rama',
      meeting: 'la reunion',
      copilot: 'el copilot',
      sprint: 'el sprint',
      task: 'la tarea',
      deliverable: 'el entregable',
      implementation: 'la implementacion',
      timelineEvent: 'el evento de historial',
    }
    return labels[entityType] || entityType
  }

  const detectChanges = <T extends Record<string, unknown>>(
    oldData: T,
    newData: Partial<T>,
    fieldsToTrack: (keyof T)[]
  ): ActivityLogChange[] => {
    const changes: ActivityLogChange[] = []

    fieldsToTrack.forEach((field) => {
      const oldValue = oldData[field]
      const newValue = newData[field]

      if (newValue !== undefined && oldValue !== newValue) {
        changes.push({
          field: String(field),
          oldValue,
          newValue,
        })
      }
    })

    return changes
  }

  return {
    logActivity,
    logCreate,
    logUpdate,
    logDelete,
    logStatusChange,
    logAssignment,
    logComment,
    detectChanges,
  }
}
